import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PawPrint, ArrowRight, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const AcceptTransfer = () => {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transferData, setTransferData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTransferDetails();
  }, [transferId]);

  const fetchTransferDetails = async () => {
    try {
      const response = await axios.get(`/transfers/${transferId}`);
      setTransferData(response.data);
      
      if (response.data.transfer.status === 'completed') {
        setError('This transfer has already been completed.');
      }
    } catch (err) {
      setError('Transfer not found or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAndPay = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`/transfers/${transferId}/pay`, {
        host_url: window.location.origin
      });
      
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Transfer Unavailable</h2>
            <p className="text-stone-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { transfer, pet } = transferData;

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <PawPrint className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              International Pet Registry
            </h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowRight className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Pet Ownership Transfer</CardTitle>
              <CardDescription>
                {transfer.from_owner_name} wants to transfer a pet to you
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Pet Details */}
              <div className="bg-stone-50 p-6 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-white rounded-lg flex items-center justify-center">
                    {pet?.photos?.length > 0 ? (
                      <img src={pet.photos[0]} alt={pet.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <span className="text-4xl">
                        {pet?.species === 'Dog' ? '🐕' : pet?.species === 'Cat' ? '🐈' : '🐾'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold">{pet?.name}</h3>
                    <p className="text-stone-600">{pet?.species} • {pet?.breed || 'Unknown breed'}</p>
                    <p className="text-sm text-stone-500">{pet?.gender} • {pet?.color}</p>
                    {pet?.microchip_number && (
                      <div className="mt-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          Microchip: {pet.microchip_number}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Transfer Details */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Transfer Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">From</p>
                    <p className="font-medium">{transfer.from_owner_name}</p>
                    <p className="text-stone-600">{transfer.from_owner_email}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">To</p>
                    <p className="font-medium">{transfer.to_owner_name}</p>
                    <p className="text-stone-600">{transfer.to_owner_email}</p>
                  </div>
                </div>
              </div>

              {/* Fee Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-blue-900">Transfer Fee</p>
                    <p className="text-sm text-blue-700">One-time payment to complete transfer</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">${transfer.transfer_fee.toFixed(2)}</p>
                </div>
              </div>

              {/* What You Get */}
              <div className="space-y-2">
                <h4 className="font-semibold">What you get:</h4>
                <ul className="space-y-2 text-sm text-stone-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Full ownership of {pet?.name} in the International Pet Registry
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Access to pet's complete medical history and records
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Ability to update pet information and contact details
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Lost pet alerts and recovery assistance
                  </li>
                </ul>
              </div>

              {/* Action Button */}
              <Button 
                onClick={handleAcceptAndPay} 
                disabled={processing}
                className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Accept Transfer & Pay ${transfer.transfer_fee.toFixed(2)}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-stone-500">
                By accepting, you agree to become the registered owner of this pet.
                Payment is processed securely via Stripe.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AcceptTransfer;
